import { useState } from "react";
import { v1 as uuidv4 } from "uuid";

import { getNativeRadioButtonClassName } from "@bokio/designsystem/components/RadioField/RadioField";
import { mergeClassNames } from "@bokio/utils/classes";

import type { ReactNode } from "react";

import * as styles from "./cardRadio.scss";

export interface CardRadioSubItem {
	key: string;
	title: ReactNode;
	description?: ReactNode;
}

export interface CardRadioProps {
	/** Unique name for the radio group */
	name: string;
	/** Value of this radio option */
	value: string;
	/** Whether the radio is selected */
	checked?: boolean;
	/** Disables the radio input */
	disabled?: boolean;
	/** Callback fired when the radio is selected */
	onChange?: (value: string) => void;
	/** Sub-items to display in the card (title + optional description pairs) */
	subItems: CardRadioSubItem[];
	/** Test ID for the label element */
	testId?: string;
	/** Test ID for the input element */
	inputTestId?: string;
}

export const CardRadio = ({
	name,
	value,
	checked,
	disabled,
	onChange,
	subItems,
	testId,
	inputTestId,
}: CardRadioProps) => {
	const [uniqueId] = useState(() => uuidv4());

	const handleChange = () => {
		if (onChange && !disabled) {
			onChange(value);
		}
	};

	return (
		<label
			htmlFor={uniqueId}
			data-testid={testId}
			data-value={value}
			data-name={name}
			className={mergeClassNames(
				styles.cardRadio,
				checked && styles.cardRadioSelected,
				disabled && styles.cardRadioDisabled,
			)}
		>
			<input
				id={uniqueId}
				type="radio"
				name={name}
				value={value}
				data-testid={inputTestId}
				onChange={handleChange}
				checked={!!checked}
				disabled={disabled}
				className={mergeClassNames(styles.radioInput, getNativeRadioButtonClassName(disabled))}
			/>
			<div className={styles.contentWrapper}>
				{subItems.map(subItem => (
					<div key={subItem.key} className={styles.subItem}>
						<div className={styles.subItemTitle}>{subItem.title}</div>
						{subItem.description && <div className={styles.subItemDescription}>{subItem.description}</div>}
					</div>
				))}
			</div>
		</label>
	);
};
